import { ReactElement, useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlinePencilSquare,
  HiOutlineArrowPath,
  HiOutlineCircleStack,
  HiOutlineTrash,
  HiOutlineEyeSlash,
  HiOutlineArrowRight,
  HiOutlineDocumentText,
} from 'react-icons/hi2';
import Button from '../../../components/Button';
import DropdownPortal from '../../../components/DropdownPortal';
import ConfigSelector from './ConfigSelector';
import { updateRun } from '../../../api/runs';
import { toast } from 'react-hot-toast';
import { useReprocess } from '../../../hooks/useReprocess';
import { SessionAnalysisType, SessionAnalysisTypeUtils } from '../../../types/SessionAnalysisType';
import { isDiscardConfig } from '../../../utils/configRole';
import { getSingletonConfigId, useRefreshSingletonSessions } from '../../../hooks/useSingletonSessions';
import { EditOperationMode } from '../../SessionView/SessionDetails/SessionConfigs/editOperationUtils';

interface RunEditMenuProps {
  run: any;
  config: any;
  configs?: any[];
  session?: any;
  sessions?: any[] | null;
  sessionsLoading?: boolean;
  invalidateRuns: any;
  configMovesToSave?: { [key: string]: () => Promise<void> };
  moveRight?: boolean;
  buttonStyle: string;
  editOpMode?: EditOperationMode;
  setEditOpMode?: (mode: EditOperationMode) => void;
  onReprocessRun?: () => void;
}

export default function RunEditMenu(props: RunEditMenuProps): ReactElement {
  const {
    run,
    config,
    configs,
    session,
    sessions,
    sessionsLoading,
    invalidateRuns,
    configMovesToSave,
    moveRight = false,
    buttonStyle,
    editOpMode,
    setEditOpMode,
    onReprocessRun,
  } = props;

  const navigate = useNavigate();
  const [showEditMenu, setShowEditMenu] = useState(false);
  const editMenuRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const { handleReprocess } = useReprocess();
  const refreshSingletonSessions = useRefreshSingletonSessions();

  // Helper function to handle singleton config fetching with retry on error
  const getSingletonConfigWithRetry = async (analysisType: SessionAnalysisType, retryCount = 0): Promise<string> => {
    try {
      return await getSingletonConfigId(analysisType);
    } catch (error) {
      // If first attempt failed, refresh cache and retry once
      if (retryCount === 0) {
        console.warn('Failed to get singleton config, refreshing cache and retrying...', error);
        await refreshSingletonSessions();
        return getSingletonConfigWithRetry(analysisType, 1);
      }
      // If retry also failed, throw the error
      throw error;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editMenuRef.current && !editMenuRef.current.contains(event.target as Node)) {
        setShowEditMenu(false);
      }
    };

    if (showEditMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEditMenu]);

  const handleReprocessRun = async (fromChunks = false) => {
    if (!run.id) {
      toast.error('Cannot reprocess planned runs');
      return;
    }

    if (!handleReprocess) {
      toast.error('Reprocessing function not available');
      return;
    }

    try {
      await handleReprocess(invalidateRuns, [run], { fromChunks });
      setShowEditMenu(false);
      if (onReprocessRun) onReprocessRun();
    } catch (error) {
      console.error('Reprocessing error:', error);
      toast.error('Error reprocessing run');
    }
  };

  const moveToDiscarded = async () => {
    try {
      const configId = await getSingletonConfigWithRetry(SessionAnalysisType.DISCARDED);
      await updateRun(run.id, { id: run.id, config_id: configId });
      invalidateRuns();
      toast.success('Run moved to Discarded');
    } catch (error) {
      console.error('Error moving run to discarded:', error);
      toast.error('Error moving run to discarded');
    }
  };

  const moveToHidden = async () => {
    try {
      const configId = await getSingletonConfigWithRetry(SessionAnalysisType.DISCARDED_ARCHIVE);
      await updateRun(run.id, { id: run.id, config_id: configId });
      invalidateRuns();
      toast.success('Run moved to Hidden');
    } catch (error) {
      console.error('Error moving run to hidden:', error);
      toast.error('Error moving run to hidden');
    }
  };

  const moveToFreeRideNonSession = async () => {
    try {
      const configId = await getSingletonConfigWithRetry(SessionAnalysisType.FREERIDE_UNSTRUCTURED);
      await updateRun(run.id, { id: run.id, config_id: configId });
      invalidateRuns();
      toast.success('Run moved to Free Ride Non-Session');
    } catch (error) {
      console.error('Error moving run to free ride non-session:', error);
      toast.error('Error moving run to free ride non-session');
    }
  };

  const removeFromSession = async () => {
    try {
      const configId = await getSingletonConfigWithRetry(SessionAnalysisType.DISCARDED);
      await updateRun(run.id, { id: run.id, config_id: configId });
      invalidateRuns();
      toast.success('Run removed from session');
    } catch (error) {
      console.error('Error removing run from session:', error);
      toast.error('Error removing run from session');
    }
  };

  const handleNotesClick = () => {
    // Use the same navigation logic as the overview button in RunEntry
    // This ensures the back button returns to the correct context
    if (config?.testsession_id) {
      navigate(`/sessions/${config.testsession_id}/runs/${run.id}/overview`, {
        state: { activateNotesEdit: true },
      });
    } else {
      navigate(`/runs/${run.id}/overview`, {
        state: { activateNotesEdit: true },
      });
    }
    setShowEditMenu(false);
  };

  return (
    <div className="cr-relative" ref={anchorRef}>
      <Button
        className={buttonStyle}
        onClick={() => setShowEditMenu(!showEditMenu)}
        icon={<HiOutlinePencilSquare className="cr-size-5" />}
      />
      <DropdownPortal anchorRef={anchorRef} open={showEditMenu} alignRight={!moveRight} offsetX={32}>
        <div
          className="cr-animate-fade-in cr-w-80 cr-rounded-md cr-bg-white cr-shadow-lg
                     cr-ring-1 cr-ring-black cr-ring-opacity-5"
          ref={editMenuRef}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="cr-py-1" role="menu" aria-orientation="vertical">
            <div
              className="cr-w-full cr-cursor-pointer cr-px-4 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700
                         hover:cr-bg-cirrusBlue-25"
              role="menuitem"
              onClick={() => {
                setShowEditMenu(false);
                if (config?.testsession_id) {
                  navigate(
                    `/sessions/${config.testsession_id}/runs/${run.id}/overview`
                  );
                } else {
                  navigate(`/runs/${run.id}/overview`);
                }
              }}
            >
              <div className="cr-flex cr-items-center cr-gap-2">
                <HiOutlineDocumentText className="cr-size-4" />
                <span>Details</span>
              </div>
            </div>
            <div className="cr-my-1 cr-border-t cr-border-gray-200"></div>
            <div
              className="cr-w-full cr-cursor-pointer cr-px-4 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700
                         hover:cr-bg-cirrusBlue-25"
              role="menuitem"
              onClick={handleNotesClick}
            >
              <div className="cr-flex cr-items-center cr-gap-2">
                <HiOutlineDocumentText className="cr-size-4" />
                <span>Add Notes</span>
              </div>
            </div>
            <div className="cr-my-1 cr-border-t cr-border-gray-200"></div>
            <div
              className="cr-w-full cr-cursor-pointer cr-px-4 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700
                         hover:cr-bg-cirrusBlue-25"
              role="menuitem"
              onClick={() => handleReprocessRun()}
            >
              <div className="cr-flex cr-items-center cr-gap-2">
                <HiOutlineArrowPath className="cr-size-4" />
                <span>Reprocess Run</span>
              </div>
            </div>
            {run?.source_episode_id && (
              <div
                className="cr-w-full cr-cursor-pointer cr-px-4 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700
                           hover:cr-bg-cirrusBlue-25"
                role="menuitem"
                title="Rebuild this run's data from the uploaded device blobs, then reprocess"
                onClick={() => handleReprocessRun(true)}
              >
                <div className="cr-flex cr-items-center cr-gap-2">
                  <HiOutlineCircleStack className="cr-size-4" />
                  <span>Reprocess from Blob Data</span>
                </div>
              </div>
            )}
            <ConfigSelector
              run={run}
              config={config}
              session={session}
              setShowEditMenu={setShowEditMenu}
              configs={configs}
              sessions={sessions || undefined}
              sessionsLoading={sessionsLoading}
              invalidateRuns={invalidateRuns}
              configMovesToSave={configMovesToSave}
              editOpMode={editOpMode}
              setEditOpMode={setEditOpMode}
            />
            {/* Move to Free Ride Non-Session - show if run is in a valid session (and not already there) */}
            {session && SessionAnalysisTypeUtils.isValid(session.analysis_type)
              && session.analysis_type !== SessionAnalysisType.FREERIDE_UNSTRUCTURED && (
              <div
                className="cr-w-full cr-cursor-pointer cr-px-4 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700
                          hover:cr-bg-cirrusBlue-25"
                role="menuitem"
                onClick={() => {
                  moveToFreeRideNonSession();
                  setShowEditMenu(false);
                }}
              >
                <div className="cr-flex cr-items-center cr-gap-2">
                  <HiOutlineArrowRight className="cr-size-4" />
                  <span>Move to Free Ride (Non-Session)</span>
                </div>
              </div>
            )}
            {/* Move to Discarded - show if run is in a valid session */}
            {(session == null || (session && SessionAnalysisTypeUtils.isValid(session.analysis_type))) && (
              <div
                className="cr-w-full cr-cursor-pointer cr-px-4 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700
                          hover:cr-bg-cirrusBlue-25"
                role="menuitem"
                onClick={() => {
                  moveToDiscarded();
                  setShowEditMenu(false);
                }}
              >
                <div className="cr-flex cr-items-center cr-gap-2">
                  <HiOutlineTrash className="cr-size-4" />
                  <span>Move to Discarded Runs</span>
                </div>
              </div>
            )}
            {/* Remove from Session - show if run is in a discarded config */}
            {config && isDiscardConfig(config) && (
              <div
                className="cr-w-full cr-cursor-pointer cr-px-4 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700
                          hover:cr-bg-cirrusBlue-25"
                role="menuitem"
                onClick={() => {
                  removeFromSession();
                  setShowEditMenu(false);
                }}
              >
                <div className="cr-flex cr-items-center cr-gap-2">
                  <HiOutlineTrash className="cr-size-4" />
                  <span>Remove from Session</span>
                </div>
              </div>
            )}
            {/* Move to Hidden - show if run is in the discarded session */}
            {session && SessionAnalysisTypeUtils.isDiscarded(session.analysis_type) && (
              <div
                className="cr-w-full cr-cursor-pointer cr-px-4 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700
                          hover:cr-bg-cirrusBlue-25"
                role="menuitem"
                onClick={() => {
                  moveToHidden();
                  setShowEditMenu(false);
                }}
              >
                <div className="cr-flex cr-items-center cr-gap-2">
                  <HiOutlineEyeSlash className="cr-size-4" />
                  <span>Move to Hidden</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </DropdownPortal>
    </div>
  );
}
